import { Modal, Spinner } from '@/components/ui';
import { useEmpresasPorCombinacion } from '@/hooks/useSysadmin';
import { AlertTriangle, Building2, Mail, User } from 'lucide-react';

interface EmpresasCombinacionModalProps {
  isOpen: boolean;
  onClose: () => void;
  combinacionId: number | undefined;
  combinacionNombre: string | undefined;
}

export function EmpresasCombinacionModal({ isOpen, onClose, combinacionId, combinacionNombre }: EmpresasCombinacionModalProps) {
  const { empresas, isLoading, error } = useEmpresasPorCombinacion(combinacionId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Empresas con esta combinación" size="lg">
      <div className="space-y-4">
        {/* Header info */}
        {combinacionNombre && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <span className="font-medium">Combinación:</span> {combinacionNombre}
            </p>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Empresas list */}
        {!isLoading && !error && (
          <>
            {empresas.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay empresas asignadas a esta combinación</p>
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-600 mb-2">
                  Se encontraron <span className="font-medium text-gray-900">{empresas.length}</span>{' '}
                  {empresas.length === 1 ? 'empresa' : 'empresas'}
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {empresas.map((empresa) => (
                    <div
                      key={empresa.nitId}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                            <h4 className="font-medium text-gray-900 truncate">{empresa.nombre}</h4>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">NIT: {empresa.nitId}</p>

                          {empresa.asesor ? (
                            <div className="bg-gray-50 rounded-md p-2.5 space-y-1.5">
                              <div className="flex items-center gap-2 text-sm">
                                <User className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                <span className="text-gray-700">{empresa.asesor.nombre}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                <a href={`mailto:${empresa.asesor.correo}`} className="text-blue-600 hover:underline">
                                  {empresa.asesor.correo}
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-center">
                              <p className="text-xs text-amber-700 font-medium">Sin asesor asignado</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
